import { timeout } from 'widgets/toolbox/util';

/**
 * @param BasicInput Base widget for extending
 * @returns Input Number class
 */
function InputNumberClassCreator(BasicInput: import('widgets/forms/BasicInput').TBasicInput) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputNumber
     * @augments BasicInput
     * @classdesc Widget used as input that accepts numbers only. This is similar to InputStepper but without button controls
     * and appropriate roles.
     * @property {string} data-widget - Widget name `inputNumber`
     * @example <caption>Example HTML structure for InputNumber</caption>
     * <div
     *     data-widget="inputNumber"
     *     data-min="${lineItem.quantityOptions.minOrderQuantity + ''}"
     *     data-max="${lineItem.quantityOptions.maxOrderQuantity + ''}"
     *     data-inform-attempt-decrease-min-val="true"
     *     data-widget-event-attemptdecreaseminval="removeProduct"
     *     data-validation-config='{"errors":{"required":"This field is required.","range":"Only values from $5 to $500 are accepted"}}'
     *>
     *    <label class="line-item-pricing-info quantity-label" for="quantity-${lineItem.UUID}">
     *        ${Resource.msg('field.selectquantity','cart',null)}
     *    </label>
     *    <div class="quantity-form-inner">
     *        <input
     *            data-ref="field"
     *            type="text"
     *            pattern="[0-9]*"
     *            inputmode="numeric"
     *            data-event-change="changeValue"
     *            value="${lineItem.quantity + ''}"
     *        />
     *     </div>
     *     <div class="invalid-feedback is-hidden" data-ref="errorFeedback" hidden="hidden"></div>
     * </div>
     */
    class InputNumber extends BasicInput {
        prevValidValue = '';

        prefs() {
            return {
                max: Number.POSITIVE_INFINITY,
                min: 0,
                informAttemptDecreaseMinVal: false,
                stayInBoundaries: false,
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.initValue = this.getValue();
        }

        /**
         * @description Executes after widget was refreshed
         */
        onRefresh(): void {
            this.initValue = this.getValue();
        }

        /**
         * @description Handle input event. On this event we allow user to insert any values, but filter only digits
         * and emit "inputchanged" event to parent widget. Also we filter values to store any valid value as prevValidValue
         * so on blur we could insert one of it.
         * @listens dom#input
         * @emits InputNumber#inputchanged
         */
        handleInput(): void {
            const currentValue = this.getValue();
            const digitsOnlyValue = currentValue.replace(/\D/g, '');

            super.setValue(digitsOnlyValue.toString(), true); // allow user to insert any values

            this.prevValidValue = this.filterValue(digitsOnlyValue);
            this.emit('inputchanged');
        }

        /**
         * @description Tries to change value of input. In case if new value is behind specific number input constrains - returns previous or default value.
         * @emits InputNumber#attemptdecreaseminval
         * @listens dom#change
         */
        changeValue() {
            if (this.getValue() === '' || this.getValue() === '-') {
                this.setValue(String(this.prevValidValue || this.initValue));
            }

            if (Number(this.getValue()) < this.prefs().min && this.prefs().informAttemptDecreaseMinVal) {
                this.onDestroy(timeout(() => {
                    /**
                     * @description Event, indicates that attempt to set number input value less than specified in constraints
                     * @event InputNumber#attemptdecreaseminval
                     */
                    this.emit('attemptdecreaseminval');
                    this.setValue(String(this.prevValidValue || this.initValue));
                }, 100));
            } else {
                this.setValue(this.getValue());
            }

            this.emit('inputchanged');
        }

        /**
         * @description Get valid input values from raw input
         * @param rawValue input value
         * @returns valid value string
         */
        filterValue(rawValue: string): string {
            const numberValue = String(rawValue).replace(/\D/g, '');
            const floatValue = parseFloat(numberValue);

            if (Number.isNaN(floatValue)) {
                return '';
            } else if (!this.prefs().stayInBoundaries) {
                return floatValue.toString();
            }

            let result = '';

            if (floatValue >= this.prefs().min && floatValue <= this.prefs().max) {
                result = floatValue.toString();
            } else if (floatValue > this.prefs().max) {
                result = String(this.prefs().max);
            } else if (floatValue < this.prefs().min) {
                result = String(this.prefs().min);
            }

            return result;
        }

        /**
         * @description Sets value for number input. Checks it on constraints first.
         * Manages input buttons less/more.
         * @param val - set this value to input
         * @param silent - if set to `true` - input should not be validated against a new value
         */
        setValue(val: string, silent = false): void {
            super.setValue(this.filterValue(val), silent);
        }

        /**
         * @description Implements number input specific logic to indicate if field value is valid.
         * @returns input validation result
         */
        isValid(): boolean {
            const isSuperValid = super.isValid();

            if (isSuperValid) {
                const val = parseFloat(this.getValue());

                if (val < this.prefs().min || val > this.prefs().max) {
                    const validation = this.prefs().validationConfig;

                    this.error = validation.errors.range || validation.errors.parse;

                    return false;
                }
            }

            return isSuperValid;
        }

        /**
         * @description Initiates field validation process. Manages validation error messages on input.
         * @returns input validation result
         */
        validate(): boolean {
            if (this.disabled) {
                return true;
            }

            const isValid = this.isValid();

            if (isValid) {
                this.clearError();
            } else {
                this.setError(this.error);
            }

            return isValid;
        }
    }

    return InputNumber;
}

export type TInputNumber = ReturnType<typeof InputNumberClassCreator>;

export type TInputNumberInstance = InstanceType<TInputNumber>;

export default InputNumberClassCreator;
